import type { FullSeafarerData } from './types/seafarer';
import { normalizeCertificateName } from '@/data/certificate-catalog';
import { detectRankTier, TIER_REQUIREMENTS, TIER_LABELS, type RankTier, type RankRequirement } from '@/data/rank-requirements';

export type CertPresenceStatus = 'present' | 'expired' | 'expiring_soon' | 'missing';

export type CompletenessItem = RankRequirement & {
  status: CertPresenceStatus;
  /** Actual cert name found in profile (may differ from canonical label). */
  foundAs?: string;
  /** Expiry date string if known. */
  expiresOn?: string | null;
};

export type ProfileCompleteness = {
  rank: string;
  tier: RankTier;
  tierLabel: string;
  items: CompletenessItem[];
  summary: {
    total: number;
    present: number;
    expired: number;
    expiringSoon: number;
    missing: number;
    /** 0-100 score (present + expiring_soon count as ½ toward score). */
    score: number;
  };
};

// ── Date helpers ───────────────────────────────────────────────────────────────

export function parseDate(date: string | null | undefined): number | null {
  if (!date) return null;
  const parts = date.trim().split('/');
  if (parts.length !== 3) return null;
  const [d, m, y] = parts.map(Number);
  if (!d || !m || !y) return null;
  const ts = Date.UTC(y, m - 1, d);
  return isNaN(ts) ? null : ts;
}

export function expiryStatus(validUpto: string | null | undefined): CertPresenceStatus {
  const ts = parseDate(validUpto);
  if (ts === null) return 'present'; // no expiry = lifetime
  const now = Date.now();
  if (ts < now) return 'expired';
  if (ts - now < 90 * 86_400_000) return 'expiring_soon';
  return 'present';
}

// ── Build a canonical-id → cert map from the full profile ─────────────────────

export type FoundCert = { name: string; validUpto: string | null | undefined };

function buildCanonicalMap(data: FullSeafarerData): Map<string, FoundCert> {
  const map = new Map<string, FoundCert>();

  const tryAdd = (name: string, validUpto?: string | null) => {
    if (!name) return;
    const match = normalizeCertificateName(name);
    if (!match) return;
    const existing = map.get(match.canonicalId);
    // Keep the one with the latest or no expiry
    if (!existing) {
      map.set(match.canonicalId, { name, validUpto });
    } else {
      const existingTs = parseDate(existing.validUpto);
      const incomingTs = parseDate(validUpto);
      // Prefer no-expiry (null) or later date
      if (incomingTs === null || (existingTs !== null && incomingTs > existingTs)) {
        map.set(match.canonicalId, { name, validUpto });
      }
    }
  };

  // Also handle specific document types that don't come through normalizeCertificateName
  const docTypeToCanonical: Record<string, string> = {
    indos:    'indos',
    cdc:      'cdc',
    passport: 'passport',
    'exn-45': 'exn45',
    exn45:    'exn45',
  };

  for (const doc of data.authorisedDocuments ?? []) {
    const typeKey = (doc.documentType ?? '').toLowerCase().replace(/[^a-z0-9]/g, '');
    for (const [key, id] of Object.entries(docTypeToCanonical)) {
      if (typeKey.includes(key.replace(/[^a-z0-9]/g, ''))) {
        const existing = map.get(id);
        if (!existing) map.set(id, { name: doc.documentType, validUpto: doc.validUpto });
        break;
      }
    }
    tryAdd(doc.documentType, doc.validUpto);
  }

  for (const course of data.modularCourses ?? []) {
    tryAdd(course.courseName, course.validUpto);
  }

  for (const coc of data.certificatesOfCompetency ?? []) {
    tryAdd(coc.grade, coc.validUpto);
    // Also handle CoC by specific tier patterns
    const grade = (coc.grade ?? '').toLowerCase();
    if (/master|class\s*i\s+(nav|deck)/i.test(grade)) tryAdd('CoC Master Class I Navigation', coc.validUpto);
    if (/chief\s+(mate|officer)|class\s*ii\s+(nav|deck)/i.test(grade)) tryAdd('Chief Mate CoC Class II', coc.validUpto);
    if (/class\s*iii|class\s*iv|oow/i.test(grade) && /nav|deck/i.test(grade)) tryAdd('OOW Certificate of Competency', coc.validUpto);
    if (/chief\s+engineer|class\s*i\s+(motor|steam|eng)/i.test(grade)) tryAdd('Chief Engineer CoC Class I', coc.validUpto);
    if (/second\s+engineer|class\s*ii\s+(motor|steam|eng)/i.test(grade)) tryAdd('Second Engineer CoC Class II', coc.validUpto);
    if (/class\s*iii|class\s*iv/i.test(grade) && /motor|steam|eng/i.test(grade)) tryAdd('Engineer Officer CoC', coc.validUpto);
    if (/eto|electro/i.test(grade)) tryAdd('ETO Certificate', coc.validUpto);
  }

  for (const coe of data.certificatesOfEndorsement ?? []) {
    tryAdd(coe.endorsementType, coe.validUpto);
  }

  for (const t of data.preSeaTraining ?? []) {
    tryAdd(t.courseName, undefined);
  }

  for (const t of data.postSeaTraining ?? []) {
    tryAdd(t.grade ?? t.courseName, undefined);
  }

  // Medical fitness comes through authorised documents as "ENG1" or similar
  if (!map.has('medical')) {
    const hasMedical = [
      ...(data.authorisedDocuments ?? []),
    ].some(d => /\beng\s*1\b|medical\s+fitness|fitness\s+certif/i.test(d.documentType ?? ''));
    if (hasMedical) {
      const doc = data.authorisedDocuments!.find(d => /\beng\s*1\b|medical\s+fitness/i.test(d.documentType ?? ''));
      if (doc) map.set('medical', { name: doc.documentType, validUpto: doc.validUpto });
    }
  }

  return map;
}

// ── CoC-level check (the catalog canonicalIds for CoC are rank-specific) ──────

function checkCocForTier(tier: RankTier, canonicalMap: Map<string, FoundCert>): CertPresenceStatus {
  // If any relevant CoC key is present, the seafarer has it
  const tierCocIds: Record<RankTier, string[]> = {
    master:           ['coc_master'],
    chief_officer:    ['coc_chief_officer'],
    officer_deck:     ['coc_oow'],
    chief_engineer:   ['coc_chief_engineer'],
    second_engineer:  ['coc_second_engineer'],
    officer_engine:   ['coc_engineer'],
    eto:              ['eto_cert'],
    rating:           [],
  };
  const ids = tierCocIds[tier];
  // Also check if profile has ANY CoC of competency (for tiers where exact mapping may fail)
  const cocFallbackIds = ['coc_master','coc_chief_officer','coc_oow','coc_chief_engineer','coc_second_engineer','coc_engineer'];
  for (const id of ids) {
    if (canonicalMap.has(id)) return expiryStatus(canonicalMap.get(id)!.validUpto);
  }
  // Fallback: any CoC present at all?
  if (ids.length > 0) {
    for (const id of cocFallbackIds) {
      if (canonicalMap.has(id)) return expiryStatus(canonicalMap.get(id)!.validUpto);
    }
  }
  return 'missing';
}

// ── Public helper: canonical cert map ────────────────────────────────────────

/** Returns the full cert presence map for the seafarer. Used by rank-progression logic. */
export function buildSeafarerCanonicalMap(data: FullSeafarerData): Map<string, FoundCert> {
  return buildCanonicalMap(data);
}

// ── Main export ───────────────────────────────────────────────────────────────

/**
 * Compute profile completeness for the seafarer's current rank.
 * Returns null if no rank can be determined.
 */
export function computeProfileCompleteness(data: FullSeafarerData): ProfileCompleteness | null {
  // Derive rank: prefer most recent sea service rank, then profile.currentRank
  const latestRank = data.seaGoingService?.length
    ? [...data.seaGoingService].reverse().find(s => s.rank)?.rank
    : undefined;
  const rank = latestRank || data.profile?.currentRank || '';
  if (!rank) return null;

  const tier = detectRankTier(rank);
  if (!tier) return null;

  const requirements = TIER_REQUIREMENTS[tier];
  const canonicalMap = buildCanonicalMap(data);

  const mfaEntry = canonicalMap.get('mfa');
  const mfaPresent = mfaEntry ? expiryStatus(mfaEntry.validUpto) !== 'missing' : false;

  const items: CompletenessItem[] = requirements.map(req => {
    // Special handling for CoC (rank-specific, not in the regular catalog)
    if (['coc_master','coc_chief_officer','coc_oow','coc_chief_engineer','coc_second_engineer','coc_engineer','eto_cert'].includes(req.canonicalId)) {
      const status = checkCocForTier(tier, canonicalMap);
      const found = canonicalMap.get(req.canonicalId);
      return { ...req, status, foundAs: found?.name, expiresOn: found?.validUpto };
    }

    // MFA (Medical First Aid) is a superset of EFA (Elementary First Aid).
    // If MFA is present and valid, treat EFA as covered.
    if (req.canonicalId === 'efa' && mfaPresent) {
      const status = expiryStatus(mfaEntry!.validUpto);
      return { ...req, status, foundAs: `${mfaEntry!.name} (covers EFA)`, expiresOn: mfaEntry!.validUpto };
    }

    const found = canonicalMap.get(req.canonicalId);
    if (!found) return { ...req, status: 'missing' as CertPresenceStatus };
    const status = expiryStatus(found.validUpto);
    return { ...req, status, foundAs: found.name, expiresOn: found.validUpto };
  });

  const counts = items.reduce(
    (acc, item) => {
      acc[item.status] = (acc[item.status] ?? 0) + 1;
      return acc;
    },
    {} as Record<CertPresenceStatus, number>,
  );

  const present = counts.present ?? 0;
  const expiringSoon = counts.expiring_soon ?? 0;
  const expired = counts.expired ?? 0;
  const missing = counts.missing ?? 0;
  const total = items.length;
  const score = total === 0 ? 0 : Math.round(((present + expiringSoon * 0.5) / total) * 100);

  return {
    rank,
    tier,
    tierLabel: TIER_LABELS[tier],
    items,
    summary: { total, present, expired, expiringSoon, missing, score },
  };
}
